import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Progress } from "../../components/ui/progress";
import { Checkbox } from "../../components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "../../components/ui/radio-group";
import { Label } from "../../components/ui/label";
import { ChevronLeft, ChevronRight, Send, Clock, AlarmClock, X } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../components/ui/alert-dialog";

export default function TakeTest() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [idx, setIdx] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());
  const submittedRef = useRef(false);

  useEffect(() => {
    api
      .get(`/me/tests/${id}`)
      .then(({ data }) => {
        setTest(data);
        setAnswers(data.questions.map(() => []));
      })
      .catch((err) => {
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
        navigate("/dashboard");
      });
  }, [id, navigate]);

  useEffect(() => {
    const t = setInterval(() => {
      setElapsed(Math.floor((Date.now() - startRef.current) / 1000));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const submit = useCallback(
    async (auto = false) => {
      if (submittedRef.current) return;
      if (!test) return;
      if (!auto) {
        const unanswered = answers.findIndex((a) => a.length === 0);
        if (unanswered !== -1) {
          toast.error(`Responde la pregunta ${unanswered + 1}`);
          setIdx(unanswered);
          return;
        }
      }
      submittedRef.current = true;
      setSubmitting(true);
      try {
        const { data } = await api.post("/me/submit", {
          test_id: id,
          answers,
          duration_seconds: elapsed,
          question_order: test.question_order || null,
          option_orders: test.option_orders || null,
        });
        if (auto) toast.warning("Tiempo agotado. Test enviado automáticamente.");
        navigate(`/result/${data.id}`);
      } catch (err) {
        submittedRef.current = false;
        toast.error(formatApiErrorDetail(err.response?.data?.detail));
      } finally {
        setSubmitting(false);
      }
    },
    [answers, elapsed, id, navigate, test]
  );

  // Time-limit auto-submit
  const limitSec = (test?.time_limit_minutes || 0) * 60;
  const remaining = limitSec > 0 ? Math.max(0, limitSec - elapsed) : null;

  useEffect(() => {
    if (limitSec > 0 && remaining === 0 && !submittedRef.current) {
      submit(true);
    }
  }, [remaining, limitSec, submit]);

  if (!test) {
    return (
      <div className="min-h-screen flex items-center justify-center text-gray-500">
        Cargando test…
      </div>
    );
  }

  const q = test.questions[idx];
  const total = test.questions.length;
  const progress = ((idx + 1) / total) * 100;
  const current = answers[idx] || [];

  const setSingle = (val) => {
    const next = [...answers];
    next[idx] = [Number(val)];
    setAnswers(next);
  };

  const toggleMulti = (val) => {
    const next = [...answers];
    const cur = new Set(next[idx]);
    if (cur.has(val)) cur.delete(val);
    else cur.add(val);
    next[idx] = Array.from(cur);
    setAnswers(next);
  };

  const formatTime = (s) => {
    const m = String(Math.floor(s / 60)).padStart(2, "0");
    const sec = String(s % 60).padStart(2, "0");
    return `${m}:${sec}`;
  };
  const lowTime = remaining !== null && remaining <= 60;

  return (
    <div className="min-h-screen bg-white">
      <div className="border-b border-gray-200 bg-white sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between mb-3 gap-3">
            <h1 className="font-display text-lg text-gray-900 truncate">{test.title}</h1>
            <div className="flex items-center gap-2">
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
                    data-testid="cancel-test-button"
                  >
                    <X className="w-4 h-4 mr-1" /> Cancelar
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent data-testid="cancel-test-dialog">
                  <AlertDialogHeader>
                    <AlertDialogTitle>¿Cancelar el test?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Volverás a tu panel sin enviar respuestas. Este intento no
                      contará como fallido y podrás empezar el test de nuevo cuando
                      quieras.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel data-testid="cancel-test-keep">
                      Seguir con el test
                    </AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        submittedRef.current = true;
                        navigate("/dashboard");
                      }}
                      className="bg-red-600 hover:bg-red-700"
                      data-testid="cancel-test-confirm"
                    >
                      Cancelar y salir
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              {remaining !== null ? (
                <div
                  className={`flex items-center gap-1 text-sm font-mono-data px-2 py-1 rounded-md border ${
                    lowTime ? "text-red-600 border-red-200 bg-red-50" : "text-gray-700 border-gray-200"
                  }`}
                  data-testid="timer-countdown"
                >
                  <AlarmClock className="w-4 h-4" /> {formatTime(remaining)}
                </div>
              ) : (
                <div className="flex items-center gap-1 text-sm font-mono-data text-gray-600">
                  <Clock className="w-4 h-4" /> {formatTime(elapsed)}
                </div>
              )}
            </div>
          </div>
          <Progress value={progress} className="h-2" />
          <p className="text-xs text-gray-500 mt-2">
            Pregunta {idx + 1} de {total} · Objetivo: {test.pass_percentage}%
            {test.time_limit_minutes ? ` · Límite: ${test.time_limit_minutes} min` : ""}
          </p>
        </div>
      </div>

      <main className="max-w-3xl mx-auto px-6 py-12">
        <Card key={idx} className="p-8 border border-gray-200 shadow-none rounded-lg fade-up">
          {q.block ? (
            <div className="flex items-center gap-2 mb-3" data-testid="question-block-pill">
              <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-brand-soft text-brand-ink/80 text-[10px] tracking-[0.2em] uppercase font-mono-data border border-[#cfe6e1]">
                {q.block}
              </span>
            </div>
          ) : null}
          <p className="text-xs text-brand font-mono-data uppercase tracking-wider mb-2">
            {q.type === "single" ? "Opción única" : q.type === "multiple" ? "Múltiple respuesta" : "Verdadero / Falso"}
          </p>
          <h2 className="font-display text-2xl text-gray-900 mb-6 leading-snug">{q.text}</h2>

          {q.type === "single" || q.type === "truefalse" ? (
            <RadioGroup
              value={current[0] != null ? current[0].toString() : ""}
              onValueChange={setSingle}
              className="space-y-3"
            >
              {q.options.map((opt, oi) => (
                <Label
                  key={oi}
                  htmlFor={`opt-${oi}`}
                  className="quiz-option flex items-center gap-3 cursor-pointer"
                  data-selected={current[0] === oi}
                  data-testid={`quiz-option-${oi}`}
                >
                  <RadioGroupItem value={oi.toString()} id={`opt-${oi}`} />
                  <span className="text-sm font-normal">{opt}</span>
                </Label>
              ))}
            </RadioGroup>
          ) : (
            <div className="space-y-3">
              {q.options.map((opt, oi) => (
                <Label
                  key={oi}
                  htmlFor={`opt-${oi}`}
                  className="quiz-option flex items-center gap-3 cursor-pointer"
                  data-selected={current.includes(oi)}
                  data-testid={`quiz-option-${oi}`}
                >
                  <Checkbox
                    id={`opt-${oi}`}
                    checked={current.includes(oi)}
                    onCheckedChange={() => toggleMulti(oi)}
                  />
                  <span className="text-sm font-normal">{opt}</span>
                </Label>
              ))}
            </div>
          )}
        </Card>

        <div className="flex items-center justify-between mt-6">
          <Button variant="outline" onClick={() => setIdx(idx - 1)} disabled={idx === 0} data-testid="prev-question">
            <ChevronLeft className="w-4 h-4 mr-1" /> Anterior
          </Button>

          {idx < total - 1 ? (
            <Button className="bg-brand hover:bg-brand-hover text-white" onClick={() => setIdx(idx + 1)} data-testid="next-question">
              Siguiente <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          ) : (
            <Button
              className="bg-emerald-600 hover:bg-emerald-700"
              onClick={() => submit(false)}
              disabled={submitting}
              data-testid="submit-test"
            >
              <Send className="w-4 h-4 mr-2" />
              {submitting ? "Enviando…" : "Enviar test"}
            </Button>
          )}
        </div>

        <div className="mt-8 flex flex-wrap gap-2 justify-center">
          {test.questions.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              data-testid={`pager-${i}`}
              className={`w-9 h-9 text-xs font-mono-data rounded-md border transition-colors ${
                i === idx
                  ? "bg-brand text-white border-brand"
                  : answers[i]?.length
                  ? "bg-brand-soft text-brand-ink border-[#bcdfd9]"
                  : "bg-white text-gray-500 border-gray-200 hover:border-gray-300"
              }`}
            >
              {i + 1}
            </button>
          ))}
        </div>
      </main>
    </div>
  );
}
